import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Package, ShoppingCart, Store, Tag, AlertCircle, ImageIcon } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const KataleProductDetails = () => {
  const { productId } = useParams()
  const navigate = useNavigate()
  const { customer } = useCustomerAuth()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [addingToCart, setAddingToCart] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchProduct()
  }, [productId])

  const getMgCode = (tenantDb) => {
    if (!tenantDb) return null
    const prefix = 'mgfina6_tenant_'
    if (tenantDb.startsWith(prefix)) {
      return tenantDb.substring(prefix.length)
    }
    const parts = tenantDb.split('_')
    return parts.length > 1 ? parts[1] : tenantDb
  }

  const fetchProduct = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await apiService.getKataleProduct(productId)
      if (response.status === 'SUCCESS' && response.data) {
        const currentProduct = response.data

        let parsedImages = []
        if (currentProduct.images) {
          try {
            const decoded = JSON.parse(currentProduct.images)
            if (Array.isArray(decoded)) {
              parsedImages = decoded
            }
          } catch (_e) {
            parsedImages = []
          }
        }

        const imageUrls = parsedImages
          .map((img) => (typeof img === 'string' ? img : img?.url))
          .filter(Boolean)

        setProduct({
          ...currentProduct,
          imageUrls,
          primary_image: currentProduct.primary_image || imageUrls[0] || null,
        })
      } else {
        setError(response.message || 'Product not found')
      }
    } catch (err) {
      console.error('Failed to fetch product details:', err)
      setError('Failed to load product details. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount || 0)
  }

  const handleAddToCart = async () => {
    if (!customer) {
      alert('Please log in to add items to cart')
      return
    }

    const buyerMemNo = customer.memberNo
    const buyerMgCode = getMgCode(customer.tenantDb)

    setAddingToCart(true)
    try {
      const response = await apiService.addToKataleCart(product.id, buyerMemNo, buyerMgCode, 1)
      if (response.status === 'SUCCESS') {
        alert('Product added to cart!')
        navigate('/katale/cart')
      } else {
        alert(response.message || 'Failed to add product to cart')
      }
    } catch (err) {
      console.error('Add to cart error:', err)
      alert('Failed to add product to cart')
    } finally {
      setAddingToCart(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/katale/products" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Products
          </Link>
          <div className="bg-white border border-red-200 rounded-lg p-6">
            <div className="flex items-center text-red-700">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>{error || 'Product not found'}</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/katale/products" className="inline-flex items-center text-gray-600 hover:text-gray-900">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Products
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="h-80 bg-gray-100 flex items-center justify-center">
                {product.primary_image ? (
                  <img src={product.primary_image} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-gray-400 text-center">
                    <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                    <span className="text-sm">No Image Available</span>
                  </div>
                )}
              </div>
              {product.imageUrls && product.imageUrls.length > 1 && (
                <div className="p-4 grid grid-cols-5 gap-2 border-t">
                  {product.imageUrls.slice(0, 5).map((img, index) => (
                    <img key={`${img}-${index}`} src={img} alt={`${product.name}-${index}`} className="h-16 w-full object-cover rounded" />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-3">
                <Tag className="h-3 w-3 mr-1" /> {product.category_name || 'Uncategorized'}
              </span>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <p className="text-sm text-gray-500 mb-4 flex items-center">
                <Store className="h-4 w-4 mr-1" />
                Seller: {product.seller_name || 'Unknown Seller'}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Price</p>
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(product.price)}</p>
                  <p className="text-xs text-gray-500">per {product.unit || 'unit'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Stock</p>
                  <p className="text-xl font-bold text-gray-900">{product.stock_quantity}</p>
                  <p className="text-xs text-gray-500">{product.unit || 'unit'} available</p>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={addingToCart || Number(product.stock_quantity) <= 0}
                className="w-full btn btn-primary disabled:opacity-50"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                {Number(product.stock_quantity) > 0
                  ? (addingToCart ? 'Adding...' : 'Add to Cart')
                  : 'Out of Stock'}
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Package className="h-4 w-4 mr-2 text-gray-500" /> Product Details
              </h2>
              <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-line">
                {product.description?.trim() ? product.description : 'No product description provided by seller.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KataleProductDetails